/**
 * 功法系统引擎
 * 匹配度计算、学习、切换、升级
 */

import type { PlayerState } from './Player';
import type { SpiritualRoot, Element } from '../data/spiritualRoots';
import type { Technique } from '../data/techniques';
import { TECHNIQUES, getLayerMultiplier, getUpgradeCost } from '../data/techniques';

const ALL_ELEMENTS: Element[] = ['金', '木', '水', '火', '土'];

/**
 * 计算功法对玩家灵根的匹配度
 * 匹配度 = Σ(灵根每个元素的功法亲和度) / 元素数
 */
export function calcMatchScore(root: SpiritualRoot, technique: Technique): number {
  let total = 0;
  for (const elem of root.elements) {
    total += technique.affinity[elem];
  }
  return Math.round((total / root.elements.length) * 100) / 100;
}

/**
 * 计算综合修炼速度倍率（功法部分）
 * = 匹配度 × 层数加成
 */
export function getTechniqueMultiplier(player: PlayerState): number {
  const tech = getPlayerTechnique(player);
  if (!tech) return 1.0; // 无功法时基础速度
  const match = calcMatchScore(player.spiritualRoot, tech);
  const layerMult = getLayerMultiplier(player.techniqueLayer ?? 1);
  return Math.round(match * layerMult * 100) / 100;
}

/** 获取玩家当前功法 */
export function getPlayerTechnique(player: PlayerState): Technique | undefined {
  if (!player.techniqueId) return undefined;
  return TECHNIQUES.find((t) => t.id === player.techniqueId);
}

/** 学习功法 */
export function learnTechnique(player: PlayerState, techId: string): { ok: boolean; message: string } {
  const tech = TECHNIQUES.find((t) => t.id === techId);
  if (!tech) return { ok: false, message: '功法不存在' };

  if (player.techniqueId === techId) {
    return { ok: false, message: `你已经学习了${tech.name}` };
  }

  const oldTech = getPlayerTechnique(player);
  player.techniqueId = techId;
  player.techniqueLayer = 1;

  const match = calcMatchScore(player.spiritualRoot, tech);
  const oldName = oldTech ? oldTech.name : '无';

  return {
    ok: true,
    message: `📖 从「${oldName}」切换至「${tech.name}」！灵根匹配度：${(match * 100).toFixed(0)}%`,
  };
}

/** 升级功法层数 */
export function upgradeTechnique(player: PlayerState): { ok: boolean; message: string } {
  const tech = getPlayerTechnique(player);
  if (!tech) return { ok: false, message: '尚未学习任何功法' };

  const currentLayer = player.techniqueLayer ?? 1;
  if (currentLayer >= tech.maxLayer) {
    return { ok: false, message: `「${tech.name}」已达最高层数（${tech.maxLayer}层）` };
  }

  const cost = getUpgradeCost(currentLayer);
  if (player.spiritStones < cost) {
    return { ok: false, message: `灵石不足！升到${currentLayer + 1}层需要 ${cost} 灵石` };
  }

  player.spiritStones -= cost;
  player.techniqueLayer = currentLayer + 1;
  const newMult = getLayerMultiplier(currentLayer + 1);

  return {
    ok: true,
    message: `⬆️ 「${tech.name}」升至${currentLayer + 1}层！修炼加成×${newMult.toFixed(2)}（消耗${cost}灵石）`,
  };
}

/**
 * 初始功法分配
 */
export function assignStartingTechnique(identityId: string): string {
  switch (identityId) {
    case 'sect_sword':
      return 'gengjin_jue';
    case 'sect_alchemy':
      return 'qingmu_changsheng';
    case 'sect_array':
      return 'houtu_guiyuan';
    case 'sect_demon':
      return 'xuesha_mogong';
    case 'master_disciple':
      // 前辈收徒：随机玄品功法
      return Math.random() < 0.5 ? 'taiyin_zhenshui' : 'lihuo_fentian';
    default:
      // 散修、被囚、被遣散
      return 'basic_breathing';
  }
}
